import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useApp } from '../../context/AppContext.tsx';
import { Article, Comment } from '../../types.ts';
import SocialShareButtons from '../common/SocialShareButtons.tsx';
import { handleImageError, getResilientFallbackImage } from '../../utils/imageUtils.ts';
import {
  Calendar,
  Eye,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Share2,
  Check,
  MessageSquare,
  Send,
  Tag,
  Clock,
  User,
  Heart,
  Edit3,
  ExternalLink,
  Search,
  Link as LinkIcon,
} from 'lucide-react';

interface ArticleDetailProps {
  slugOrId: string;
}

export default function ArticleDetail({ slugOrId }: ArticleDetailProps) {
  const {
    articles,
    navigate,
    showToast,
    settings,
    user,
    users,
    setSelectedCategory,
    setSearchQuery,
    openAuthorModal,
  } = useApp();
  const [article, setArticle] = useState<Article | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [liked, setLiked] = useState(false);

  // Comment form state
  const [authorName, setAuthorName] = useState('');
  const [authorEmail, setAuthorEmail] = useState('');
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  const primaryColor = settings?.theme.primaryColor || '#059669';

  useEffect(() => {
    async function loadArticleData() {
      setLoading(true);
      try {
        const res = await fetch(`/api/articles/${slugOrId}`);
        const data = await res.json();
        if (data.success && data.data) {
          setArticle(data.data);

          // Fetch comments
          const commRes = await fetch(`/api/comments?articleId=${data.data.id}&status=approved`);
          const commData = await commRes.json();
          if (commData.success) {
            setComments(commData.data || []);
          }
        } else {
          setArticle(null);
        }
      } catch (err) {
        console.error('Error fetching article detail:', err);
        setArticle(null);
      } finally {
        setLoading(false);
      }
    }
    loadArticleData();
  }, [slugOrId]);

  useEffect(() => {
    if (article?.title) {
      const suffix = settings?.pageTabTitles?.titleSuffix !== undefined ? settings.pageTabTitles.titleSuffix : ` | ${settings?.siteName || 'ALY LAW FIRM'}`;
      document.title = `${article.title}${suffix}`;
    }
  }, [article?.title, settings?.pageTabTitles?.titleSuffix, settings?.siteName]);

  const handleShare = (platform: 'whatsapp' | 'twitter' | 'facebook' | 'copy') => {
    const url = window.location.href;
    const title = article?.title || '';

    if (platform === 'whatsapp') {
      window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(title + ' ' + url)}`, '_blank');
    } else if (platform === 'twitter') {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`, '_blank');
    } else if (platform === 'facebook') {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
    } else if (platform === 'copy') {
      navigator.clipboard.writeText(url);
      setCopied(true);
      showToast('Link artikel berhasil disalin ke clipboard!', 'success');
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!article || !authorName.trim() || !commentText.trim()) {
      showToast('Mohon lengkapi nama dan komentar Anda', 'error');
      return;
    }

    setSubmittingComment(true);
    try {
      const res = await fetch('/api/comments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          articleId: article.id,
          authorName: authorName.trim(),
          authorEmail: authorEmail.trim(),
          content: commentText.trim(),
        }),
      });
      const data = await res.json();
      if (data.success && data.data) {
        setComments([data.data, ...comments]);
        setCommentText('');
        showToast('Your comment has been submitted successfully!', 'success');
      }
    } catch (err) {
      console.error(err);
      showToast('Failed to submit comment', 'error');
    } finally {
      setSubmittingComment(false);
    }
  };

  if (loading) {
    return (
      <div className="py-20 text-center max-w-2xl mx-auto">
        <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-sm font-semibold text-slate-600">Loading article...</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="py-20 text-center max-w-2xl mx-auto px-4">
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Article Not Found</h2>
        <p className="text-sm text-slate-500 mb-6">The article you requested could not be found or has been moved.</p>
        <button
          onClick={() => navigate('blog')}
          className="px-5 py-2.5 bg-slate-900 text-white rounded-xl font-bold text-sm"
        >
          Back to Articles
        </button>
      </div>
    );
  }

  // Related articles
  const relatedArticles = articles
    .filter((a) => a.id !== article.id && (a.categoryId === article.categoryId || a.status === 'published'))
    .slice(0, 3);

  // Estimate reading time
  const wordsCount = (article.content || '').split(/\s+/).length;
  const readTimeMinutes = Math.max(1, Math.ceil(wordsCount / 200));

  // Resolve author avatar & profile from dashboard users dynamically
  const authorUser = users?.find(
    (u) =>
      article?.author &&
      (u.name.trim().toLowerCase() === article.author.trim().toLowerCase() ||
        u.username.trim().toLowerCase() === article.author.trim().toLowerCase() ||
        u.email.trim().toLowerCase() === article.author.trim().toLowerCase())
  );

  const authorPhoto =
    authorUser?.avatar ||
    article?.authorAvatar ||
    (user && article?.author && (user.name === article.author || user.username === article.author) ? user.avatar : '') ||
    'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=200&q=80';

  return (
    <div
      id="article-detail-view"
      className="py-8 sm:py-12"
      style={{ backgroundColor: settings?.theme?.articlePageBgColor || 'var(--article-page-bg, #F7F5EF)' }}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Back navigation & Category */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <button
            onClick={() => navigate('blog')}
            className="flex items-center gap-2 text-xs font-bold transition-colors"
            style={{ color: 'var(--article-secondary-text, #5C6B62)' }}
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Articles</span>
          </button>

          <div className="flex items-center gap-2">
            <span
              onClick={() => {
                if (article.categoryId) setSelectedCategory(article.categoryId);
                navigate('blog');
              }}
              className="px-3 py-1 rounded-full text-xs font-extrabold uppercase tracking-wide text-white shadow-xs cursor-pointer hover:opacity-90 transition-opacity"
              style={{ backgroundColor: primaryColor }}
              title="Filter articles by this category"
            >
              {article.categoryName}
            </span>

            {user && (
              <button
                onClick={() => {
                  try {
                    localStorage.setItem('admin_target_tab', 'articles');
                    localStorage.setItem('admin_edit_article_id', article.id);
                  } catch (e) {}
                  navigate('admin');
                }}
                className="px-3 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded-full text-xs font-bold flex items-center gap-1 shadow-xs transition-colors cursor-pointer"
                title="Edit this article in Admin Dashboard"
              >
                <Edit3 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Edit Article</span>
              </button>
            )}
          </div>
        </div>

        {/* Title */}
        <h1
          className="text-2xl sm:text-4xl md:text-5xl font-extrabold leading-tight sm:leading-tight mb-3 tracking-tight text-center max-w-3xl mx-auto"
          style={{ color: settings?.theme?.articleTitleColor || 'var(--article-title, #1B4332)' }}
        >
          {article.title}
        </h1>

        {/* Article Metadata Bar */}
        <div
          className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 py-2 mb-4 border-y text-xs text-center"
          style={{ borderColor: 'var(--article-card-border, #E3EBDD)', color: 'var(--article-secondary-text, #5C6B62)' }}
        >
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6">
            <div
              onClick={() => openAuthorModal(article.author, authorPhoto)}
              className="flex items-center gap-2 cursor-pointer group"
              title={`View profile & bio for ${article.author}`}
            >
              <img
                src={authorPhoto}
                alt={article.author}
                className="w-7 h-7 rounded-full border border-slate-300 object-cover group-hover:border-emerald-500 transition-colors shadow-2xs"
              />
              <span className="font-bold leading-tight group-hover:underline transition-colors" style={{ color: 'var(--article-body-text, #1F2A24)' }}>
                {article.author}
              </span>
            </div>

            <div className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5 opacity-70" />
              <span>
                {new Date(article.createdAt).toLocaleDateString('en-US', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
            </div>
          </div>

          <div className="hidden sm:block h-4 w-px bg-slate-300" />

          {/* Social Share Buttons */}
          <SocialShareButtons
            title={article.title}
            summary={article.summary}
            imageUrl={article.coverImage}
            variant="compact"
          />
        </div>

        {/* Featured Cover Image & Takarir (Caption) */}
        {(article.coverImage || article.title) && (
          <div className="mb-4">
            <div className="rounded-2xl overflow-hidden shadow-md aspect-16/9 bg-slate-900 border" style={{ borderColor: 'var(--article-card-border, #E3EBDD)' }}>
              <img
                src={article.coverImage || getResilientFallbackImage(article.id, article.categoryName)}
                alt={article.title}
                onError={(e) => handleImageError(e, article.id, article.categoryName)}
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
              />
            </div>
            {article.imageCaption && (
              <p className="text-xs text-left italic mt-1.5" style={{ color: 'var(--article-secondary-text, #5C6B62)' }}>
                Photo: {article.imageCaption}
              </p>
            )}
          </div>
        )}

        {/* Content Body with 65-75ch Reading Container */}
        <div className="mb-8">
          <div
            className="article-prose text-base sm:text-lg lg:text-xl leading-relaxed lg:leading-loose font-sans w-full max-w-none"
            style={{ color: 'var(--article-body-text, #1F2A24)' }}
          >
            {article.content && (article.content.trim().startsWith('<') || /<[a-z][\s\S]*>/i.test(article.content)) ? (
              <div 
                className="article-html-body text-base sm:text-lg lg:text-xl leading-relaxed lg:leading-loose"
                style={{ color: 'var(--article-body-text, #1F2A24)' }}
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            ) : (
              <ReactMarkdown
                components={{
                  p: ({ children }) => (
                    <p className="mb-4 sm:mb-6 leading-relaxed lg:leading-loose font-sans text-base sm:text-lg lg:text-xl block" style={{ color: 'var(--article-body-text, #1F2A24)' }}>
                      {children}
                    </p>
                  ),
                  h2: ({ children }) => (
                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-black mt-8 mb-4 tracking-tight border-b pb-2" style={{ color: 'var(--article-title, #1B4332)', borderColor: 'var(--article-card-border, #E3EBDD)' }}>
                      {children}
                    </h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-extrabold mt-6 mb-3" style={{ color: 'var(--article-title, #1B4332)' }}>
                      {children}
                    </h3>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc list-outside ml-6 space-y-2 mb-6" style={{ color: 'var(--article-body-text, #1F2A24)' }}>
                      {children}
                    </ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="list-decimal list-outside ml-6 space-y-2 mb-6" style={{ color: 'var(--article-body-text, #1F2A24)' }}>
                      {children}
                    </ol>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote
                      style={{
                        backgroundColor: 'var(--blockquote-bg, #EAF4EC)',
                        borderLeft: '4px solid var(--blockquote-border, #2D6A4F)',
                        color: 'var(--article-secondary-text, #5C6B62)',
                      }}
                      className="p-4 rounded-r-2xl italic my-6 font-medium"
                    >
                      {children}
                    </blockquote>
                  ),
                  img: ({ src, alt }) => (
                    <span className="block my-6">
                      <img src={src} alt={alt || ''} className="w-full rounded-2xl shadow-md max-h-[500px] object-cover" />
                      {alt && <span className="block text-left text-xs italic mt-2" style={{ color: 'var(--article-secondary-text, #5C6B62)' }}>{alt}</span>}
                    </span>
                  ),
                }}
              >
                {article.content ? article.content.replace(/\r\n/g, '\n').replace(/([^\n])\n([^\n#*->|1-9!\[<])/g, '$1\n\n$2') : ''}
              </ReactMarkdown>
            )}
          </div>

          {/* Recommended Read Inline Box */}
          {relatedArticles.length > 1 && (
            <div
              onClick={() => navigate('article-detail', relatedArticles[1].slug || relatedArticles[1].id)}
              className="my-6 p-3.5 sm:p-4 rounded-xl border-l-4 shadow-xs flex items-center justify-between gap-3 transition-all group cursor-pointer"
              style={{
                backgroundColor: 'var(--blockquote-bg, #EAF4EC)',
                borderLeft: '4px solid var(--blockquote-border, #2D6A4F)',
                borderColor: 'var(--article-card-border, #E3EBDD)',
              }}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <span
                  className="shrink-0 px-2.5 py-1 text-white text-[10px] sm:text-[11px] font-extrabold uppercase tracking-wider rounded-md flex items-center gap-1"
                  style={{ backgroundColor: 'var(--primary-color, #1B4332)' }}
                >
                  <BookOpen className="w-3.5 h-3.5" />
                  Recommended Read
                </span>
                <h4
                  className="text-xs sm:text-sm font-bold line-clamp-1 transition-colors"
                  style={{ color: 'var(--article-title, #1B4332)' }}
                >
                  {relatedArticles[1].title}
                </h4>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-all shrink-0" />
            </div>
          )}

          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <div
              className="mt-8 pt-6 border-t flex flex-wrap items-center gap-2"
              style={{ borderColor: 'var(--article-card-border, #E3EBDD)' }}
            >
              <Tag className="w-4 h-4" style={{ color: 'var(--article-secondary-text, #5C6B62)' }} />
              <span className="text-xs font-bold mr-1" style={{ color: 'var(--article-secondary-text, #5C6B62)' }}>Related Tags:</span>
              {article.tags.map((tag, idx) => {
                const cleanTag = tag.trim().replace(/^#/, '');
                const customUrl =
                  article.tagUrls?.[cleanTag] ||
                  article.tagUrls?.[tag] ||
                  settings?.tagLinks?.[cleanTag] ||
                  settings?.tagLinks?.[tag];
                const finalUrl = customUrl ? customUrl.trim() : `/tag/${encodeURIComponent(cleanTag)}`;
                const isExternal = /^https?:\/\//i.test(finalUrl) || /^wa\.me/i.test(finalUrl) || /^mailto:/i.test(finalUrl) || /^tel:/i.test(finalUrl);
                const resolvedHref = finalUrl.startsWith('wa.me') ? `https://${finalUrl}` : finalUrl;

                return (
                  <a
                    key={idx}
                    href={resolvedHref}
                    target={isExternal ? '_blank' : '_self'}
                    rel={isExternal ? 'noopener noreferrer' : undefined}
                    onClick={(e) => {
                      if (!isExternal) {
                        e.preventDefault();
                        if (customUrl) {
                          const cleanPath = finalUrl.replace(/^\/+/, '');
                          if (cleanPath.startsWith('produk/') || cleanPath.startsWith('product/') || cleanPath.startsWith('services/')) {
                            navigate('product-detail', cleanPath.split('/')[1]);
                            return;
                          }
                          if (cleanPath.startsWith('page/') || cleanPath.startsWith('halaman/')) {
                            navigate('custom-page', cleanPath.split('/')[1]);
                            return;
                          }
                          if (cleanPath === 'kontak' || cleanPath === 'contact') {
                            navigate('contact');
                            return;
                          }
                          if (cleanPath === 'tentang' || cleanPath === 'about') {
                            navigate('about');
                            return;
                          }
                          if (cleanPath === 'portofolio' || cleanPath === 'portfolio') {
                            navigate('portfolio');
                            return;
                          }
                          window.location.href = finalUrl;
                        } else {
                          setSearchQuery(cleanTag);
                          navigate('blog', undefined, { tag: cleanTag });
                        }
                      }
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer shadow-2xs border bg-white hover:bg-emerald-50"
                    style={{
                      borderColor: 'var(--article-card-border, #E3EBDD)',
                      color: 'var(--primary-color, #1B4332)',
                    }}
                    title={customUrl ? `Open link: ${finalUrl}` : `Search articles tagged #${cleanTag}`}
                  >
                    <span className="font-extrabold" style={{ color: 'var(--accent-color, #2D6A4F)' }}>#</span>
                    <span>{cleanTag}</span>
                    {customUrl ? (
                      <ExternalLink className="w-3 h-3 shrink-0" style={{ color: 'var(--accent-color, #2D6A4F)' }} />
                    ) : (
                      <Search className="w-3 h-3 text-slate-400 shrink-0" />
                    )}
                  </a>
                );
              })}
            </div>
          )}
        </div>

        {/* Author Bio Profile Card */}
        <div
          className="rounded-2xl p-5 sm:p-6 mb-8 flex flex-col sm:flex-row items-center sm:items-start gap-4 text-center sm:text-left shadow-2xs border"
          style={{
            backgroundColor: 'var(--article-card-bg, #FFFFFF)',
            borderColor: 'var(--article-card-border, #E3EBDD)',
          }}
        >
          <img
            src={authorPhoto}
            alt={article.author}
            onClick={() => openAuthorModal(article.author, authorPhoto)}
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-white shadow-md shrink-0 cursor-pointer hover:scale-105 transition-transform"
            title="Click to view author bio profile"
          />
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-1">
              <div>
                <span
                  className="text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-md inline-block mb-1"
                  style={{
                    backgroundColor: 'var(--blockquote-bg, #EAF4EC)',
                    color: 'var(--primary-color, #1B4332)',
                  }}
                >
                  {settings?.articleAuthorBioRole ||
                    (authorUser?.role === 'admin'
                      ? 'Founder & Advokat Utama'
                      : authorUser?.role === 'author'
                      ? 'Advokat & Praktisi Hukum'
                      : authorUser?.role === 'editor'
                      ? 'Redaksi & Peneliti Hukum'
                      : 'Tim Advokat & Penasihat Hukum')}
                </span>
                <h3
                  onClick={() => openAuthorModal(article.author, authorPhoto)}
                  className="text-base sm:text-lg font-extrabold leading-snug cursor-pointer transition-colors"
                  style={{ color: 'var(--article-title, #1B4332)' }}
                  title="Click to view full author profile"
                >
                  {article.author}
                </h3>
              </div>
              <div className="flex items-center gap-2 self-center sm:self-auto">
                {user && (
                  <button
                    type="button"
                    onClick={() => {
                      try {
                        localStorage.setItem('bellva_admin_active_tab', 'articles');
                        localStorage.setItem('admin_open_author_bio_modal', 'true');
                      } catch (e) {}
                      navigate('admin');
                    }}
                    className="px-2.5 py-1.5 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs rounded-xl shadow-2xs transition-all cursor-pointer flex items-center gap-1 shrink-0"
                    title="Ubah teks bio / validasi penulis di Dashboard Admin"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Ubah Teks Bio</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => openAuthorModal(article.author, authorPhoto)}
                  className="px-3 py-1.5 border font-bold text-xs rounded-xl shadow-2xs transition-all cursor-pointer flex items-center gap-1 shrink-0"
                  style={{
                    backgroundColor: 'var(--blockquote-bg, #EAF4EC)',
                    borderColor: 'var(--article-card-border, #E3EBDD)',
                    color: 'var(--primary-color, #1B4332)',
                  }}
                >
                  <span>Profile Modal</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery(article.author);
                    navigate('blog');
                  }}
                  className="px-3 py-1.5 bg-white hover:bg-slate-50 border font-bold text-xs rounded-xl shadow-2xs transition-all cursor-pointer flex items-center gap-1 shrink-0"
                  style={{
                    borderColor: 'var(--article-card-border, #E3EBDD)',
                    color: 'var(--article-secondary-text, #5C6B62)',
                  }}
                >
                  <span>All Articles</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <p className="text-xs leading-relaxed mt-1" style={{ color: 'var(--article-secondary-text, #5C6B62)' }}>
              {authorUser?.bio?.trim() ? (
                authorUser.bio
              ) : settings?.articleAuthorBioNote ? (
                settings.articleAuthorBioNote.replace(/\{author\}/g, article.author)
              ) : (
                <>
                  Artikel ini ditelaah dan dipublikasikan secara resmi oleh <strong>{article.author}</strong> di Kantor Hukum ALY LAW FIRM untuk memberikan wawasan, analisis, dan solusi hukum yang terpercaya bagi masyarakat dan klien.
                </>
              )}
            </p>
          </div>
        </div>

        {/* Social Share Box */}
        <SocialShareButtons
          title={article.title}
          summary={article.summary}
          imageUrl={article.coverImage}
          variant="full"
          className="mb-8"
        />

        {/* Soft-selling CTA Box */}
        <div
          className="text-white p-6 sm:p-8 rounded-2xl shadow-lg mb-12 flex flex-col sm:flex-row items-center justify-between gap-6 border"
          style={{
            backgroundColor: 'var(--footer-bg, #12301F)',
            borderColor: 'var(--accent-color, #2D6A4F)',
          }}
        >
          <div>
            <span
              className="text-xs font-bold uppercase tracking-wider block mb-1"
              style={{ color: 'var(--light-accent, #95D5B2)' }}
            >
              Konsultasi Hukum 24 Jam
            </span>
            <h3 className="text-lg sm:text-xl font-bold text-white">
              Butuh Konsultasi atau Pendampingan Hukum?
            </h3>
            <p className="text-xs mt-1 max-w-lg" style={{ color: 'var(--footer-text, #D8E8DC)' }}>
              Hubungi tim advokat ALY LAW FIRM langsung melalui WhatsApp untuk konsultasi awal mengenai perkara, litigasi, kontrak bisnis, maupun kebutuhan legalitas Anda.
            </p>
          </div>

          <a
            href={`https://wa.me/${(settings?.contact?.whatsappNumber || '6282130955059').replace(/[^0-9]/g, '')}?text=Halo%20Tim%20ALY%20LAW%20FIRM,%20saya%20membaca%20artikel%20"${encodeURIComponent(article.title)}"%20dan%20ingin%20berkonsultasi%20mengenai%20hukum.`}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 px-5 py-3 rounded-xl text-white text-xs font-bold shadow-md hover:opacity-95 transition-transform hover:scale-105"
            style={{ backgroundColor: 'var(--accent-color, #2D6A4F)' }}
          >
            <span>Konsultasi via WhatsApp</span>
          </a>
        </div>

        {/* Interactive Comments Column */}
        <section
          id="comments-section"
          className="p-6 sm:p-8 rounded-2xl border shadow-xs mb-12"
          style={{
            backgroundColor: 'var(--article-card-bg, #FFFFFF)',
            borderColor: 'var(--article-card-border, #E3EBDD)',
          }}
        >
          <h3
            className="text-lg font-bold flex items-center gap-2 mb-6 pb-3 border-b"
            style={{
              color: 'var(--article-title, #1B4332)',
              borderColor: 'var(--article-card-border, #E3EBDD)',
            }}
          >
            <MessageSquare className="w-5 h-5" style={{ color: 'var(--accent-color, #2D6A4F)' }} />
            <span>Reader Discussion ({comments.length})</span>
          </h3>

          {/* New Comment Submission Form */}
          <form
            onSubmit={handleCommentSubmit}
            className="mb-8 p-5 rounded-xl border"
            style={{
              backgroundColor: 'var(--article-page-bg, #F7F5EF)',
              borderColor: 'var(--article-card-border, #E3EBDD)',
            }}
          >
            <h4
              className="text-xs font-bold mb-3 uppercase tracking-wider"
              style={{ color: 'var(--article-title, #1B4332)' }}
            >
              Leave a Comment / Response
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-[11px] font-bold block mb-1" style={{ color: 'var(--article-body-text, #1F2A24)' }}>Full Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Dr. Jane Anderson"
                  value={authorName}
                  onChange={(e) => setAuthorName(e.target.value)}
                  className="w-full px-3 py-2 text-xs bg-white border rounded-lg focus:outline-hidden focus:ring-2 focus:ring-emerald-500"
                  style={{
                    borderColor: 'var(--article-card-border, #E3EBDD)',
                    color: 'var(--article-body-text, #1F2A24)',
                  }}
                />
              </div>

              <div>
                <label className="text-[11px] font-bold block mb-1" style={{ color: 'var(--article-body-text, #1F2A24)' }}>Email (Optional)</label>
                <input
                  type="email"
                  placeholder="name@enterprise.com"
                  value={authorEmail}
                  onChange={(e) => setAuthorEmail(e.target.value)}
                  className="w-full px-3 py-2 text-xs bg-white border rounded-lg focus:outline-hidden focus:ring-2 focus:ring-emerald-500"
                  style={{
                    borderColor: 'var(--article-card-border, #E3EBDD)',
                    color: 'var(--article-body-text, #1F2A24)',
                  }}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="text-[11px] font-bold block mb-1" style={{ color: 'var(--article-body-text, #1F2A24)' }}>Comment Content *</label>
              <textarea
                required
                rows={3}
                placeholder="Share your perspectives, questions, or industry insights regarding this topic..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                className="w-full px-3 py-2 text-xs bg-white border rounded-lg focus:outline-hidden focus:ring-2 focus:ring-emerald-500"
                style={{
                  borderColor: 'var(--article-card-border, #E3EBDD)',
                  color: 'var(--article-body-text, #1F2A24)',
                }}
              />
            </div>

            <button
              type="submit"
              disabled={submittingComment}
              className="px-4 py-2 rounded-xl text-white text-xs font-bold shadow-xs flex items-center gap-1.5 hover:opacity-95 transition-all"
              style={{ backgroundColor: 'var(--accent-color, #2D6A4F)' }}
            >
              <Send className="w-3.5 h-3.5" />
              <span>{submittingComment ? 'Submitting...' : 'Post Comment'}</span>
            </button>
          </form>

          {/* Comments List */}
          <div className="space-y-4">
            {comments.length === 0 ? (
              <p className="text-xs italic py-4 text-center" style={{ color: 'var(--article-secondary-text, #5C6B62)' }}>
                No reader responses yet. Be the first to share your perspective!
              </p>
            ) : (
              comments.map((comm) => (
                <div
                  key={comm.id}
                  className="p-4 rounded-xl border flex items-start gap-3"
                  style={{
                    backgroundColor: 'var(--article-page-bg, #F7F5EF)',
                    borderColor: 'var(--article-card-border, #E3EBDD)',
                  }}
                >
                  <div
                    className="w-8 h-8 rounded-full text-white flex items-center justify-center font-bold text-xs shrink-0"
                    style={{ backgroundColor: 'var(--primary-color, #1B4332)' }}
                  >
                    {comm.authorName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h5 className="font-bold text-xs" style={{ color: 'var(--article-title, #1B4332)' }}>{comm.authorName}</h5>
                      <span className="text-[10px]" style={{ color: 'var(--article-secondary-text, #5C6B62)' }}>
                        {new Date(comm.createdAt).toLocaleDateString('en-US', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                    <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--article-body-text, #1F2A24)' }}>{comm.content}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Related Articles Section */}
        {relatedArticles.length > 0 && (
          <section className="mt-12">
            <h3 className="text-xl font-bold mb-6" style={{ color: 'var(--article-title, #1B4332)' }}>Related Articles & Insights</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {relatedArticles.map((rel) => (
                <div
                  key={rel.id}
                  onClick={() => navigate('article-detail', rel.slug || rel.id)}
                  className="rounded-xl border overflow-hidden shadow-xs hover:shadow-md transition-all cursor-pointer group flex flex-col"
                  style={{
                    backgroundColor: 'var(--article-card-bg, #FFFFFF)',
                    borderColor: 'var(--article-card-border, #E3EBDD)',
                  }}
                >
                  <div className="aspect-16/10 overflow-hidden bg-slate-100">
                    <img
                      src={rel.coverImage || getResilientFallbackImage(rel.id, rel.categoryName)}
                      alt={rel.title}
                      onError={(e) => handleImageError(e, rel.id, rel.categoryName)}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                  <div className="p-4 flex-1 flex flex-col justify-between">
                    {(() => {
                      const blogPrefix = (settings?.pageSlugs?.articles || settings?.pageSlugs?.blogSlug || 'blog').replace(/^\/+|\/+$/g, '');
                      const relUrl = `/${blogPrefix}/${rel.slug || rel.id}`;
                      return (
                        <a
                          href={relUrl}
                          onClick={(e) => e.stopPropagation()}
                          className="font-bold text-sm sm:text-base line-clamp-2 leading-snug transition-colors"
                          style={{ color: 'var(--article-title, #1B4332)' }}
                          title="Open article"
                        >
                          {rel.title}
                        </a>
                      );
                    })()}
                    <span className="text-[10px] mt-2 block" style={{ color: 'var(--article-secondary-text, #5C6B62)' }}>
                      {new Date(rel.createdAt).toLocaleDateString('en-US', {
                        day: 'numeric',
                        month: 'short',
                      })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
